import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { HERO_IMAGES } from "@/data/heroImages";
import { Shield, Scale, Sparkles, Sun, Hammer, HandHeart, ArrowRight } from "lucide-react";

const HOUSE_IMG = "/assets/libs-house.webp";

const VIRTUES = [
  { icon: Hammer, label: "Force", who: "Les étudiants", color: "#002362", desc: "Démontrer la force de leur jeunesse, leur énergie, leur audace." },
  { icon: Sun, label: "Tempérance", who: "Le corps enseignant", color: "#ED2939", desc: "Faire preuve de mesure et de constance dans l'administration des cours." },
  { icon: Shield, label: "Prudence", who: "L'administration", color: "#002362", desc: "Gérer l'école avec discernement et rigueur dans chaque décision." },
  { icon: Scale, label: "Justice", who: "La direction", color: "#ED2939", desc: "Être juste dans chaque arbitrage stratégique, au service de tous." },
];

const FOUNDATIONS = [
  { icon: Hammer, label: "Le Travail", num: "01", desc: "L'engagement quotidien, la discipline et l'exigence personnelle." },
  { icon: HandHeart, label: "Le Service", num: "02", desc: "Se placer au service de l'autre, la qualité du service à l'étudiant comme priorité absolue." },
];

const VirtueCard = ({ v, index }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = v.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -8 }}
      className="relative bg-white border border-navy-100 overflow-hidden isolate cursor-default"
      style={{
        boxShadow: hovered
          ? "0 30px 70px -20px rgba(0,35,98,0.30)"
          : "0 8px 28px -16px rgba(0,35,98,0.10)",
      }}
      data-testid={`virtue-${v.label.toLowerCase()}`}
    >
      <div className={`absolute inset-0 origin-bottom transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] -z-10 ${hovered ? "scale-y-100" : "scale-y-0"}`}
        style={{ background: `linear-gradient(135deg, ${v.color}, #000e27)` }} />
      <svg className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-700 ${hovered ? "opacity-[0.07]" : "opacity-0"}`} preserveAspectRatio="none">
        <defs><pattern id={`vp-${index}`} width="16" height="16" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1" fill="white" /></pattern></defs>
        <rect width="100%" height="100%" fill={`url(#vp-${index})`} />
      </svg>
      <span className={`absolute -top-2 right-3 font-display text-[140px] leading-none select-none pointer-events-none transition-colors duration-500 ${hovered ? "text-white/[0.07]" : "text-navy-700/[0.05]"}`} aria-hidden>
        {String(index + 1).padStart(2, "0")}
      </span>

      <div className="relative p-8 md:p-10 min-h-[280px] flex flex-col">
        <motion.div
          animate={{ rotate: hovered ? -8 : -4, scale: hovered ? 1.05 : 1 }}
          transition={{ duration: 0.5 }}
          className="relative w-14 h-14 mb-6"
        >
          <div className={`absolute inset-0 transition-colors duration-500 rounded-lg ${hovered ? "bg-white/20" : "bg-navy-50"}`} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className={`h-6 w-6 transition-colors duration-500 ${hovered ? "text-white" : "text-navy-500"}`} strokeWidth={1.5} />
          </div>
        </motion.div>

        <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 transition-colors duration-500 ${hovered ? "text-white/60" : "text-navy-400"}`}>{v.who}</div>
        <h3 className={`font-display text-3xl md:text-4xl mb-3 transition-colors duration-500 ${hovered ? "text-white" : "text-navy-700"}`}>{v.label}</h3>
        <p className={`text-[14px] leading-relaxed flex-1 transition-colors duration-500 ${hovered ? "text-white/80" : "text-[#555]"}`}>{v.desc}</p>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[3px] flex overflow-hidden">
        <span className={`flex-1 origin-left transition-transform duration-500 ${hovered ? "scale-x-100" : "scale-x-0"}`} style={{ background: "#002362" }} />
        <span className={`flex-1 origin-left transition-transform duration-[600ms] ${hovered ? "scale-x-100" : "scale-x-0"}`} style={{ background: "#fff" }} />
        <span className={`flex-1 origin-left transition-transform duration-700 ${hovered ? "scale-x-100" : "scale-x-0"}`} style={{ background: "#ED2939" }} />
      </div>
    </motion.div>
  );
};

export default function NosValeursPage() {
  return (
    <>
      <PageHero
        eyebrow="Nos valeurs"
        title="Une école,"
        italicWord="des valeurs"
        subtitle="L'École de Commerce de Lyon s'inscrit dans une démarche d'amélioration continue inspirée du Toyotisme. La qualité du service à l'étudiant est notre priorité absolue."
        crumb="Nos valeurs"
        image={HERO_IMAGES.nosValeurs}
      />

      {/* La LIBS House */}
      <section className="section-padding bg-white">
        <div className="container-edge">
          <div className="grid grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="col-span-12 lg:col-span-5 order-2 lg:order-1"
            >
              <div className="overline mb-4">La Libs House</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-navy-700 leading-tight mb-6">
                Notre démarche d'<em className="not-italic red-shine">amélioration continue</em>.
              </h2>
              <p className="text-[#444] text-[16px] leading-[1.75] mb-5">
                La Libs House est la <strong className="text-navy-700">représentation graphique de la démarche d'amélioration continue</strong> dans laquelle s'inscrit l'École de Commerce de Lyon.
              </p>
              <p className="text-[#444] text-[16px] leading-[1.75]">
                Inspirée du Toyotisme, cette démarche est placée au cœur de nos préoccupations. L'ECL a fixé la <strong className="text-navy-700">qualité du service à l'étudiant</strong> comme sa principale priorité.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="col-span-12 lg:col-span-7 order-1 lg:order-2"
            >
              <div className="relative bg-[#FAF8F3] p-8 md:p-12">
                <div className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 border-navy-500" />
                <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-red-500" />
                <img
                  src={HOUSE_IMG}
                  alt="Les valeurs de l'École de Commerce de Lyon, La Libs House"
                  className="w-full h-auto object-contain"
                  data-testid="libs-house-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Les 4 vertus cardinales */}
      <section className="section-padding bg-[#FAF8F3]">
        <div className="container-edge">
          <div className="max-w-3xl mb-14">
            <div className="overline mb-4">Des valeurs fortes</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight mb-6">
              Les <em className="not-italic red-shine">4 vertus cardinales</em>.
            </h2>
            <p className="text-[#555] text-[16px] leading-relaxed">
              La Libs House représente des valeurs fortes comme la Force, la Tempérance, la Prudence et la Justice, soient les 4 vertus cardinales. Ce sont les piliers qui soutiennent nos travaux. Chaque partie prenante de l'Ecole de Commerce de Lyon, c'est à dire les étudiants, les professeurs, l'administration ou la direction ont à cœur de cultiver la vertu qui caractérise leur corps d'appartenance.
              Ainsi les étudiants démontreront la Force de leur jeunesse. Tandis que le corps enseignant fera preuve de Tempérance dans l'administration de ses cours, l'administration de Prudence dans la gestion de l'école. Alors que la direction devra être Juste dans chacun de ses arbitrages stratégiques.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {VIRTUES.map((v, i) => <VirtueCard key={v.label} v={v} index={i} />)}
          </div>
        </div>
      </section>

      {/* Fondations */}
      <section className="section-padding bg-white">
        <div className="container-edge">
          <div className="grid grid-cols-12 gap-10 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="col-span-12 lg:col-span-5"
            >
              <div className="overline mb-4">Nos fondations</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight mb-6">
                Le Travail et le Service, fondations de l'<em className="not-italic red-shine">Excellence</em>.
              </h2>
              <p className="text-[#555] text-[16px] leading-relaxed">
                Surtout, le Travail et le Service, opportunément placés à la base de notre édifice, sont les qualités essentielles sans lesquelles l'Excellence ne peut être atteinte. Ces fondations qui sont nécessaires à la réalisation de notre devise feront l'efficience de nos apprenants. Car seules de solides fondations garantiront l'emploi à nos étudiants tout au long de leur vie professionnelle.
              </p>
            </motion.div>

            <div className="col-span-12 lg:col-span-7 space-y-5">
              {FOUNDATIONS.map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.label}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.15 }}
                    className="relative bg-[#FAF8F3] border border-navy-100 p-8 overflow-hidden group hover:shadow-md transition-shadow"
                    data-testid={`foundation-${f.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <span className="absolute -top-2 right-4 font-display text-[120px] leading-none select-none text-navy-700/[0.05] group-hover:text-navy-700/[0.08] transition-colors" aria-hidden>{f.num}</span>
                    <div className="absolute top-0 left-0 w-1 h-full bg-navy-500 group-hover:bg-red-500 transition-colors duration-500" />
                    <div className="flex items-start gap-6 relative">
                      <div className="w-14 h-14 bg-navy-500 group-hover:bg-red-500 transition-colors duration-500 flex items-center justify-center shrink-0">
                        <Icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-display text-2xl md:text-3xl text-navy-700 mb-2 group-hover:text-navy-500 transition-colors">{f.label}</h3>
                        <p className="text-[#555] text-[15px] leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Vision long terme */}
      <section className="section-padding relative overflow-hidden bg-[#FAF8F3] border-y border-navy-100">

        <div className="container-edge relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Sparkles className="h-10 w-10 text-red-500 mx-auto mb-6" strokeWidth={1.4} />
            <div className="overline mb-5">Vision long terme</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight mb-8">
              Garantir l'emploi <em className="not-italic red-shine">tout au long</em> de la vie professionnelle.
            </h2>
            <p className="text-[#444] text-[16px] md:text-[17px] leading-[1.8] mb-5">
              En effet, passé les ans, l'énergie de la jeunesse se tarira à l'érosion de la vie. La disponibilité du célibat souvent disparaîtra pour laisser la place à la parentalité et la sédentarité s'établira.
            </p>
            <p className="text-[#444] text-[16px] md:text-[17px] leading-[1.8] mb-12">
              Alors seuls l'expérience acquit au fils des ans, le réseau et la Corporation établis dès l'entrée à l'Ecole de Commerce de Lyon, garantiront l'efficacité, et donc l'emploi de nos étudiants devenus seniors, et leur permettront de rester des atouts précieux pour leurs entreprises que nous nous employons à cultiver.
            </p>

            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/ecole/mot-du-president" className="btn-red inline-flex items-center gap-2" data-testid="valeurs-cta-president">
                Lire le mot du Président
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/formations" className="btn-outline-navy inline-flex items-center gap-2" data-testid="valeurs-cta-formations">
                Nos formations
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
